export interface StatRow {
  eventType: string;
  entityType: string;
  day: string;
  count: number;
}

export interface UserStats {
  userId: string;
  userName: string;
  total: number;
  lastActive: string;
  rows: StatRow[];
}

export interface Bucket {
  key: string;
  label: string;
  color: string;
  events: string[];
}

// Audit event types grouped into 5 readable buckets, each with a
// colorblind-safe color (validated against CVD separation).
export const BUCKETS: Bucket[] = [
  {
    key: 'status',
    label: 'Status changes',
    color: '#AE3EC9',
    events: ['STATUS_CHANGE', 'MARK_AS_SENT'],
  },
  {
    key: 'edited',
    label: 'Edits',
    color: '#4C6EF5',
    events: ['UPDATE', 'MEDICAL_REQUEST_UPDATE', 'BULK_UPDATE'],
  },
  {
    key: 'created',
    label: 'Created',
    color: '#0CA678',
    events: ['CREATE', 'MEDICAL_REQUEST_CREATE'],
  },
  {
    key: 'other',
    label: 'Other actions',
    color: '#F08C00',
    events: ['UPLOAD_TO_FILEVINE', 'PROVIDER_VERIFIED', 'ATTACHMENT_ADDED'],
  },
  {
    key: 'deleted',
    label: 'Deleted',
    color: '#E64980',
    events: ['DELETE', 'MEDICAL_REQUEST_DELETE', 'ATTACHMENT_REMOVED'],
  },
];

export const bucketOfEvent = (eventType: string): Bucket =>
  BUCKETS.find((b) => b.events.includes(eventType)) || BUCKETS.find((b) => b.key === 'other')!;

export const EVENT_LABELS: Record<string, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  STATUS_CHANGE: 'Status change',
  MEDICAL_REQUEST_CREATE: 'Medical request created',
  MEDICAL_REQUEST_UPDATE: 'Medical request updated',
  MEDICAL_REQUEST_DELETE: 'Medical request deleted',
  BULK_UPDATE: 'Bulk update',
  MARK_AS_SENT: 'Marked as sent',
  UPLOAD_TO_FILEVINE: 'Uploaded to FileVine',
  PROVIDER_VERIFIED: 'Provider verified',
  ATTACHMENT_ADDED: 'Attachment added',
  ATTACHMENT_REMOVED: 'Attachment removed',
};

export const ENTITY_LABELS: Record<string, string> = {
  REQUEST: 'Requests',
  MEDICAL_REQUEST: 'Medical requests',
  FIRM: 'Law firms',
  PATIENT: 'Patients',
  PROVIDER: 'Providers',
  USER: 'Users',
  PROVIDER_CONNECTION: 'Provider connections',
  PROVIDER_NOTE: 'Provider notes',
  REQUEST_NOTE: 'Request notes',
  PATIENT_FORM: 'Patient forms',
};

export const RANGE_OPTIONS = [7, 14, 30];

const pad = (n: number) => String(n).padStart(2, '0');

// Day keys are UTC to match the backend's TO_CHAR(createdAt) grouping.
export const toDayKey = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

export interface Window {
  startDate: string;
  endDate: string;
  prevStartDate: string;
  prevEndDate: string;
  dayList: string[];
}

export function computeWindow(rangeDays: number, now = new Date()): Window {
  const end = now;
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  start.setUTCDate(start.getUTCDate() - (rangeDays - 1));
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(start);
  prevStart.setUTCDate(prevStart.getUTCDate() - rangeDays);

  const dayList: string[] = [];
  const cursor = new Date(start);
  for (let i = 0; i < rangeDays; i++) {
    dayList.push(toDayKey(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    prevStartDate: prevStart.toISOString(),
    prevEndDate: prevEnd.toISOString(),
    dayList,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const fmtDayShort = (dayKey: string) => {
  const [, m, d] = dayKey.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
};

export const fmtDateTime = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export interface RollingTrendWindow {
  startDate: string;
  endDate: string;
  dayList: string[];
  // Weekly sample points, ascending (oldest first, today last)
  sampleDayKeys: string[];
}

/**
 * A rolling-sum trend: each sample point is the total for the trailing
 * `rollingDays` days ending on that date, sampled every 7 days across
 * `weeks` weeks. This smooths out the day-to-day/period-to-period noise
 * that makes raw period-over-period percent changes unreliable (e.g. a
 * single quiet week reading as an 18600% swing).
 */
export function computeRollingTrendWindow(now = new Date(), weeks = 52, rollingDays = 30): RollingTrendWindow {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (weeks * 7 + rollingDays - 1));

  const dayList: string[] = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    dayList.push(toDayKey(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  const sampleDayKeys: string[] = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - w * 7);
    sampleDayKeys.push(toDayKey(d));
  }

  return {startDate: start.toISOString(), endDate: now.toISOString(), dayList, sampleDayKeys};
}

/** Trailing `rollingDays`-day sum ending on each of `sampleDayKeys`, read off a day-indexed totals array. */
export function rollingSumsAt(dayList: string[], dailyTotals: number[], sampleDayKeys: string[], rollingDays = 30): number[] {
  const dayIndex: Record<string, number> = {};
  dayList.forEach((d, i) => (dayIndex[d] = i));
  return sampleDayKeys.map((key) => {
    const endIdx = dayIndex[key];
    if (endIdx === undefined) return 0;
    const startIdx = Math.max(0, endIdx - rollingDays + 1);
    let sum = 0;
    for (let i = startIdx; i <= endIdx; i++) sum += dailyTotals[i] || 0;
    return sum;
  });
}

export interface UserSummary {
  totalsByBucket: Record<string, number>;
  dailyByBucket: Record<string, number[]>;
  dailyTotals: number[];
  totalsByEntity: {label: string; value: number}[];
}

export function summarize(user: UserStats, dayList: string[]): UserSummary {
  const dayIndex: Record<string, number> = {};
  dayList.forEach((d, i) => (dayIndex[d] = i));

  const totalsByBucket: Record<string, number> = {};
  const dailyByBucket: Record<string, number[]> = {};
  BUCKETS.forEach((b) => {
    totalsByBucket[b.key] = 0;
    dailyByBucket[b.key] = dayList.map(() => 0);
  });
  const dailyTotals = dayList.map(() => 0);
  const entityTotals: Record<string, number> = {};

  user.rows.forEach((row) => {
    const bucket = bucketOfEvent(row.eventType);
    totalsByBucket[bucket.key] += row.count;
    const di = dayIndex[row.day];
    if (di !== undefined) {
      dailyByBucket[bucket.key][di] += row.count;
      dailyTotals[di] += row.count;
    }
    entityTotals[row.entityType] = (entityTotals[row.entityType] || 0) + row.count;
  });

  const totalsByEntity = Object.entries(entityTotals)
    .map(([key, value]) => ({label: ENTITY_LABELS[key] || key, value}))
    .sort((a, b) => b.value - a.value);

  return {totalsByBucket, dailyByBucket, dailyTotals, totalsByEntity};
}
